import type { ExtraBags } from "@b2bportal/air-baggage-api";
import type { Person } from "@b2bportal/air-booking-api";
import { Product } from "@b2bportal/purchase-api";
import {
  FlightSelectors,
  ParentState,
  setContextWithKey,
} from "@hopper-b2b/checkout";
import { parsePhoneNumber } from "@hopper-b2b/utilities";
import { assign } from "xstate";

import { getSessionInfo } from "../selectors";
import type { FlightMachineContext } from "../types";

const DEFAULT_COUNTRY_CODE = "55";

/**
 * Helper to get passenger name - matches the pattern used in SeatSelections component
 */
const getPaxName = (person: Person) => {
  return [person.givenName, person.surname].join(" ").replace(/\s+/g, " ");
};

export const commonActions = {
  setInitialContactInformation: assign(
    (context: FlightMachineContext, _event) => {
      const sessionUserInfo = getSessionInfo({ context })?.userInfoResponse;
      const phoneNumber = sessionUserInfo?.phoneNumber;

      const parsedPhone = parsePhoneNumber(phoneNumber);
      const initialContactInfo = {
        countryCode: `+${parsedPhone.countryCode || DEFAULT_COUNTRY_CODE}`,
        phoneNumber: parsedPhone.nationalNumber,
        email: sessionUserInfo?.email || "",
      };

      return setContextWithKey(
        context,
        `${ParentState.contactInformation}.contactInfo`,
        initialContactInfo
      );
    }
  ),

  /**
   * Re-associates bag selections with current passengers.
   * Preserves bag selections for passengers that are still selected,
   * and discards selections for passengers that were removed.
   */
  reassociateBagsWithCurrentPassengers: assign(
    (context: FlightMachineContext, _event) => {
      const oldPassengerBagsMap: Map<string, ExtraBags> =
        context[ParentState.bagsSelection]?.passengerBagsMap;

      // If no bags selected, nothing to do
      if (!oldPassengerBagsMap || oldPassengerBagsMap.size === 0) {
        // Ensure cart products are cleared as well
        context[ParentState.bagsSelection].passengerBagsMap = new Map();
        context[ParentState.cartUpdate].addProducts =
          context[ParentState.cartUpdate].addProducts?.filter(
            (p) => p.type !== Product.AirBaggage
          ) || [];
        return context;
      }

      // Get current selected passengers
      const selectedPassengerIds =
        context[ParentState.passengerInformation]?.selectedPassengerIds || [];

      // Build new map keeping only passengers that still exist
      // This preserves the correct passenger ID -> bags association
      const newPassengerBagsMap = new Map<string, ExtraBags>();
      for (const [passengerId, bags] of oldPassengerBagsMap.entries()) {
        // Only keep bag selection if this passenger is still selected
        if (selectedPassengerIds.includes(passengerId)) {
          newPassengerBagsMap.set(passengerId, bags);
        }
      }

      // Update context
      context[ParentState.bagsSelection].passengerBagsMap = newPassengerBagsMap;

      // Also update the cart products
      const shoppedTripId = FlightSelectors.getQuotedShoppedTripId({ context });
      const selectedFareId = FlightSelectors.getSelectedFareId({ context });
      const productsToAdd = context[ParentState.cartUpdate].addProducts || [];
      const filterOutBagsFromProducts = productsToAdd.filter(
        (p) => p.type !== Product.AirBaggage
      );

      // Calculate total bags
      let totalBags = 0;
      for (const bags of newPassengerBagsMap.values()) {
        totalBags += bags.checkedBags + bags.carryOnBags + bags.personalItems;
      }

      // Only add bag product if there are bags selected
      if (totalBags > 0) {
        const newBagProduct = {
          type: Product.AirBaggage,
          value: {
            tripId: shoppedTripId ?? "",
            fareId: selectedFareId,
            selection: {
              ExtraBagSelection: "PerPassengerExtraBagSelection" as const,
              bagsPerPassenger: Object.fromEntries(newPassengerBagsMap),
            },
          },
        };
        filterOutBagsFromProducts.push(newBagProduct);
      }

      context[ParentState.cartUpdate].addProducts = filterOutBagsFromProducts;

      return context;
    }
  ),

  /**
   * Re-associates seat selections with current passengers.
   * Preserves seat selections for passengers that are still selected,
   * and discards selections for passengers that were removed.
   * Also updates passenger names in case they were edited.
   */
  reassociateSeatsWithCurrentPassengers: assign(
    (context: FlightMachineContext, _event) => {
      const selectedSeats = context[ParentState.seatSelection]?.selectedSeats;
      const selectedSeatSegments =
        context[ParentState.seatSelection]?.selectedSeatSegments;

      // If no seats selected, nothing to do
      if (!selectedSeats || selectedSeats.length === 0) {
        context[ParentState.seatSelection].selectedSeats = [];
        context[ParentState.seatSelection].selectedSeatSegments = [];
        context[ParentState.cartUpdate].addProducts =
          context[ParentState.cartUpdate].addProducts?.filter(
            (p) => p.type !== Product.Seats
          ) || [];
        return context;
      }

      // Get current selected passengers
      const selectedPassengerIds =
        context[ParentState.passengerInformation]?.selectedPassengerIds || [];
      const userPassengers =
        context[ParentState.passengerInformation]?.userPassengers || [];

      // Filter seats to keep only those for passengers that are still selected
      // Also update passenger names in case they were edited
      const updatedSeats = selectedSeats
        .map((seat) => {
          // Only keep seat if this passenger is still selected
          if (!selectedPassengerIds.includes(seat.person_id)) {
            return null;
          }

          // Update the passenger name (in case it was edited)
          const passenger = userPassengers.find((p) => p.id === seat.person_id);

          return {
            ...seat,
            pax: passenger ? getPaxName(passenger) : "",
          };
        })
        .filter((seat): seat is NonNullable<typeof seat> => seat !== null);

      // Update selectedSeatSegments - filter out seats for removed passengers
      const updatedSeatSegments = selectedSeatSegments?.map((segment) => ({
        ...segment,
        seats: (segment.seats || [])
          .map((seat) => {
            // Find the corresponding seat in updatedSeats
            const matchingSeat = updatedSeats.find(
              (s) => s.seat_id === seat.seat_id
            );
            // If seat was removed (passenger no longer selected), exclude it
            if (!matchingSeat) return null;
            return { ...seat, pax: matchingSeat.pax };
          })
          .filter((seat): seat is NonNullable<typeof seat> => seat !== null),
      }));

      // Update context
      context[ParentState.seatSelection].selectedSeats = updatedSeats;
      context[ParentState.seatSelection].selectedSeatSegments =
        updatedSeatSegments || [];

      // Also update the cart products
      const shoppedTripId = FlightSelectors.getQuotedShoppedTripId({ context });
      const bookingSessionId = FlightSelectors.getQuoteBookingSessionId({
        context,
      });
      const productsToAdd = context[ParentState.cartUpdate].addProducts || [];
      const filterOutSeatFromProducts = productsToAdd.filter(
        (p) => p.type !== Product.Seats
      );

      // Only add seat product if there are still seats after filtering
      if (updatedSeats.length > 0) {
        const { reservationId, seatsClientToken } =
          context.cartQuote.quoteBreakdown?.products?.find(
            (p) =>
              p.product.type === Product.Flight &&
              p.product.value.shoppedTripId === shoppedTripId
          )?.product?.value ?? {};

        const newSeatProduct = {
          type: Product.Seats,
          value: {
            command: { seats: updatedSeats },
            shoppedProductId: shoppedTripId as string,
            bookingSessionId,
            seatsClientToken,
            reservationId,
          },
        };
        filterOutSeatFromProducts.push(newSeatProduct);
      }

      context[ParentState.cartUpdate].addProducts = filterOutSeatFromProducts;

      return context;
    }
  ),
};
